import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './SyriaProtestMap.module.css';

const FILTERS = [
    { key: 'all',   label: 'الإجمالي' },
    { key: 'month', label: 'آخر شهر' },
    { key: 'week',  label: 'آخر أسبوع' },
];

// GeoJSON uses "مدينة دمشق" — map to DB name "دمشق"
const NAME_MAP = { 'مدينة دمشق': 'دمشق' };
const dbName = (geoName) => NAME_MAP[geoName] ?? geoName;

function choroplethColor(t) {
    if (t <= 0)  return null;
    if (t < 0.2) return '#FFFF66';  // yellow (low)
    if (t < 0.4) return '#FFCC00';  // gold
    if (t < 0.6) return '#FF9900';  // orange
    if (t < 0.8) return '#FF4400';  // deep orange
    return '#CC0000';               // red (high)
}

export default function SyriaProtestMap({ data }) {
    const [filter, setFilter]   = useState('all');
    const [geojson, setGeojson] = useState(null);
    const containerRef = useRef(null);
    const mapRef       = useRef(null);
    const layerRef     = useRef(null);

    // Load GeoJSON once
    useEffect(() => {
        fetch('/syria_governorates.geojson')
            .then((r) => r.json())
            .then(setGeojson)
            .catch(console.error);
    }, []);

    // Init map once
    useEffect(() => {
        if (mapRef.current) return;

        mapRef.current = L.map(containerRef.current, {
            center: [35.0, 38.5],
            zoom: 6,
            zoomControl: true,
            scrollWheelZoom: false,
            attributionControl: false,
        });

        L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
            subdomains: 'abcd',
            maxZoom: 10,
        }).addTo(mapRef.current);

        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
            layerRef.current = null;
        };
    }, []);

    // Redraw choropleth when filter, data, or geojson changes
    useEffect(() => {
        const govData = data?.by_governorate;
        if (!mapRef.current || !govData?.length || !geojson) return;

        if (layerRef.current) { layerRef.current.remove(); layerRef.current = null; }

        const pick = (g) =>
            filter === 'week'  ? (g.week_protests  ?? 0) :
            filter === 'month' ? (g.month_protests ?? 0) :
            (g.total_protests ?? 0);

        const max    = Math.max(...govData.map(pick), 1);
        const period = filter === 'week' ? 'آخر أسبوع' : filter === 'month' ? 'آخر شهر' : 'إجمالي';

        layerRef.current = L.geoJSON(geojson, {
            style: (feature) => {
                const name  = dbName(feature.properties.NAME_ARA);
                const gov   = govData.find((g) => g.name === name);
                const count = gov ? pick(gov) : 0;
                const color = choroplethColor(count / max);
                return {
                    fillColor:   color ?? '#1e293b',
                    fillOpacity: count > 0 ? 0.78 : 0.12,
                    color:       '#475569',
                    weight:      1,
                    dashArray:   count === 0 ? '4 4' : '',
                };
            },
            onEachFeature: (feature, layer) => {
                const name  = dbName(feature.properties.NAME_ARA);
                const gov   = govData.find((g) => g.name === name);
                const count = gov ? pick(gov) : 0;
                layer.bindTooltip(
                    `<div style="direction:rtl;text-align:right;font-family:Almarai,sans-serif;min-width:130px">
                        <b style="font-size:14px;color:#f1f5f9">${name}</b><br/>
                        <span style="font-size:12px;color:#94a3b8">${period}: </span>
                        <span style="font-size:14px;font-weight:700;color:#f97316">${count}</span>
                        <span style="font-size:11px;color:#94a3b8"> احتجاج</span>
                    </div>`,
                    { sticky: true, opacity: 1, className: 'spm-tip' }
                );
                layer.on('mouseover', function () {
                    this.setStyle({ weight: 2, color: '#fbbf24', fillOpacity: count > 0 ? 0.95 : 0.28 });
                    this.bringToFront();
                });
                layer.on('mouseout', function () {
                    layerRef.current?.resetStyle(this);
                });
            },
        }).addTo(mapRef.current);

    }, [filter, data, geojson]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.controls}>
                <span className={styles.label}>الفترة:</span>
                <div className={styles.pills}>
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            className={`${styles.pill} ${filter === f.key ? styles.pillActive : ''}`}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            <div ref={containerRef} className={styles.map} />

            <div className={styles.legend}>
                <span className={styles.legendTxt}>أقل</span>
                {['#7f1d1d','#b91c1c','#ef4444','#f97316','#fbbf24'].map((c, i) => (
                    <div key={i} className={styles.legendStop} style={{ background: c }} />
                ))}
                <span className={styles.legendTxt}>أكثر</span>
                <span className={styles.legendEmpty}>░ بلا نشاط</span>
            </div>
        </div>
    );
}
